/**
 * UserPreferences Component
 * Full dropdown menu with all user settings
 * Created: 2025-12-23 14:55 EST
 */

import { useState, useEffect, useRef } from 'react'
import { usePreferences, ALL_SPORTSBOOKS, LEGAL_STATES } from './PreferencesContext'
import { useAuthStore } from '../../../stores/authStore'
import './UserPreferences.css'

function UserPreferences({ isOpen, onClose, onOpenAuth }) {
  const {
    preferences,
    selectedBooks,
    state,
    oddsFormat,
    unitSize,
    bankroll,
    notifications,
    theme,
    compactView,
    showImpliedProbability,
    setSelectedBooks,
    setState,
    setOddsFormat,
    setUnitSize,
    setBankroll,
    setNotifications,
    setTheme,
    setCompactView,
    setShowImpliedProbability,
    getAvailableBooks,
  } = usePreferences()

  // Auth state
  const { user, isAuthenticated, tier, signOut } = useAuthStore()

  // Collapsible sections
  const [sections, setSections] = useState({
    sportsbooks: true,
    state: false,
    oddsFormat: false,
    bankroll: false,
    notifications: false,
    display: false,
  })

  const menuRef = useRef(null)

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const toggleSection = (section) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  // Sportsbook handlers
  const toggleBook = (bookId) => {
    if (selectedBooks.includes(bookId)) {
      setSelectedBooks(selectedBooks.filter(b => b !== bookId))
    } else {
      setSelectedBooks([...selectedBooks, bookId])
    }
  }

  const selectAllBooks = () => {
    setSelectedBooks(ALL_SPORTSBOOKS.map(b => b.id))
  }

  const deselectAllBooks = () => {
    setSelectedBooks([])
  }

  if (!isOpen) return null

  const availableBooks = getAvailableBooks()

  return (
    <div className="user-prefs-dropdown" ref={menuRef}>
      <div className="user-prefs-header">
        <i className="fas fa-sliders-h"></i>
        <span>Preferences</span>
        <button className="user-prefs-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="user-prefs-content">
        {/* ============================================
            ACCOUNT SECTION
            ============================================ */}
        <div className="prefs-section prefs-account-section">
          {isAuthenticated ? (
            <div className="prefs-account-logged-in">
              <div className="prefs-user-info">
                <div className="prefs-user-avatar">
                  <i className="fas fa-user-circle"></i>
                </div>
                <div className="prefs-user-details">
                  <span className="prefs-user-email">{user?.email}</span>
                  <span className="prefs-user-tier">
                    <i className="fas fa-crown"></i> {tier?.toUpperCase() || 'FREE'} Plan
                  </span>
                </div>
              </div>
              <button
                className="prefs-auth-btn prefs-signout-btn"
                onClick={() => {
                  signOut()
                  onClose()
                }}
              >
                <i className="fas fa-sign-out-alt"></i>
                Sign Out
              </button>
            </div>
          ) : (
            <div className="prefs-account-buttons">
              <button
                className="prefs-auth-btn prefs-login-btn"
                onClick={() => onOpenAuth('login')}
              >
                <i className="fas fa-sign-in-alt"></i>
                Sign In
              </button>
              <button
                className="prefs-auth-btn prefs-signup-btn"
                onClick={() => onOpenAuth('signup')}
              >
                <i className="fas fa-user-plus"></i>
                Sign Up
              </button>
            </div>
          )}
        </div>

        {/* ============================================
            SPORTSBOOKS SECTION
            ============================================ */}
        <div className="prefs-section">
          <button
            className="prefs-section-toggle"
            onClick={() => toggleSection('sportsbooks')}
          >
            <span><i className="fas fa-book"></i> Sportsbooks</span>
            <i className={`fas fa-chevron-${sections.sportsbooks ? 'up' : 'down'}`}></i>
          </button>
          {sections.sportsbooks && (
            <div className="prefs-section-content">
              <div className="prefs-select-all">
                <button onClick={selectAllBooks}>Select All</button>
                <button onClick={deselectAllBooks}>Deselect All</button>
              </div>
              <div className="prefs-checkbox-grid">
                {ALL_SPORTSBOOKS.map(book => (
                  <label key={book.id} className="prefs-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedBooks.includes(book.id)}
                      onChange={() => toggleBook(book.id)}
                    />
                    <span className="checkmark"></span>
                    <span className="label-text">{book.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ============================================
            STATE SECTION
            ============================================ */}
        <div className="prefs-section">
          <button
            className="prefs-section-toggle"
            onClick={() => toggleSection('state')}
          >
            <span><i className="fas fa-map-marker-alt"></i> State: {state}</span>
            <i className={`fas fa-chevron-${sections.state ? 'up' : 'down'}`}></i>
          </button>
          {sections.state && (
            <div className="prefs-section-content">
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="prefs-select"
              >
                {LEGAL_STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
              <p className="prefs-hint">
                {availableBooks.length} sportsbooks available in {state}
              </p>
            </div>
          )}
        </div>

        {/* ============================================
            ODDS FORMAT SECTION
            ============================================ */}
        <div className="prefs-section">
          <button
            className="prefs-section-toggle"
            onClick={() => toggleSection('oddsFormat')}
          >
            <span><i className="fas fa-percentage"></i> Odds Format</span>
            <i className={`fas fa-chevron-${sections.oddsFormat ? 'up' : 'down'}`}></i>
          </button>
          {sections.oddsFormat && (
            <div className="prefs-section-content">
              <div className="prefs-radio-group">
                {[
                  { id: 'american', label: 'American', example: '-110' },
                  { id: 'decimal', label: 'Decimal', example: '1.91' },
                  { id: 'fractional', label: 'Fractional', example: '10/11' },
                  { id: 'implied', label: 'Implied %', example: '52.4%' },
                ].map(format => (
                  <label key={format.id} className="prefs-radio">
                    <input
                      type="radio"
                      name="oddsFormat"
                      value={format.id}
                      checked={oddsFormat === format.id}
                      onChange={() => setOddsFormat(format.id)}
                    />
                    <span className="radio-dot"></span>
                    <span className="label-text">{format.label}</span>
                    <span className="label-example">{format.example}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ============================================
            BANKROLL SECTION
            ============================================ */}
        <div className="prefs-section">
          <button
            className="prefs-section-toggle"
            onClick={() => toggleSection('bankroll')}
          >
            <span><i className="fas fa-dollar-sign"></i> Unit Size / Bankroll</span>
            <i className={`fas fa-chevron-${sections.bankroll ? 'up' : 'down'}`}></i>
          </button>
          {sections.bankroll && (
            <div className="prefs-section-content">
              <div className="prefs-input-group">
                <label>Unit Size ($)</label>
                <input
                  type="number"
                  value={unitSize}
                  onChange={(e) => setUnitSize(Number(e.target.value))}
                  min="1"
                  step="1"
                />
              </div>
              <div className="prefs-input-group">
                <label>Total Bankroll ($)</label>
                <input
                  type="number"
                  value={bankroll}
                  onChange={(e) => setBankroll(Number(e.target.value))}
                  min="1"
                  step="1"
                />
              </div>
              <p className="prefs-hint">
                1 unit = {((unitSize / bankroll) * 100).toFixed(1)}% of bankroll
              </p>
            </div>
          )}
        </div>

        {/* ============================================
            NOTIFICATIONS SECTION
            ============================================ */}
        <div className="prefs-section">
          <button
            className="prefs-section-toggle"
            onClick={() => toggleSection('notifications')}
          >
            <span><i className="fas fa-bell"></i> Notifications</span>
            <i className={`fas fa-chevron-${sections.notifications ? 'up' : 'down'}`}></i>
          </button>
          {sections.notifications && (
            <div className="prefs-section-content">
              <div className="prefs-toggle-list">
                {[
                  { id: 'arbitrage', label: 'Arbitrage Alerts', icon: 'fas fa-balance-scale' },
                  { id: 'lineMovement', label: 'Line Movement Alerts', icon: 'fas fa-chart-line' },
                  { id: 'gameStart', label: 'Game Start Reminders', icon: 'fas fa-clock' },
                  { id: 'emailNotifications', label: 'Email Notifications', icon: 'fas fa-envelope' },
                ].map(item => (
                  <label key={item.id} className="prefs-toggle">
                    <span><i className={item.icon}></i> {item.label}</span>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notifications[item.id]}
                        onChange={(e) => setNotifications({ [item.id]: e.target.checked })}
                      />
                      <span className="toggle-slider"></span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ============================================
            DISPLAY SECTION
            ============================================ */}
        <div className="prefs-section">
          <button
            className="prefs-section-toggle"
            onClick={() => toggleSection('display')}
          >
            <span><i className="fas fa-palette"></i> Display</span>
            <i className={`fas fa-chevron-${sections.display ? 'up' : 'down'}`}></i>
          </button>
          {sections.display && (
            <div className="prefs-section-content">
              <div className="prefs-toggle-list">
                <label className="prefs-toggle">
                  <span><i className="fas fa-moon"></i> Dark Theme</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={theme === 'dark'}
                      onChange={(e) => setTheme(e.target.checked ? 'dark' : 'light')}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
                <label className="prefs-toggle">
                  <span><i className="fas fa-compress"></i> Compact View</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={compactView}
                      onChange={(e) => setCompactView(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
                <label className="prefs-toggle">
                  <span><i className="fas fa-percentage"></i> Implied Probability</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={showImpliedProbability}
                      onChange={(e) => setShowImpliedProbability(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </div>
              <div className="prefs-hint">
                <i className="fas fa-info-circle"></i>
                Hover over odds to see implied probability
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserPreferences
